import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMount, useUnmount } from "react-use";
import { CloudUpload } from "@mui/icons-material";
import { Box, Button, Divider, MenuItem, Select, SelectChangeEvent, Slider, SliderValueLabelProps, Tooltip, Typography } from "@mui/material";
import { ColDef, GridOptions, RowDoubleClickedEvent } from "ag-grid-community";
import { Feature } from "geojson";
import { FeatureIdentifier } from "mapbox-gl";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { selectLayerByVersionAndLayerKey, selectVersionConfig, useGetFeaturesQuery, useGetVersionConfigQuery } from "fond/api";
import DrawHandler from "fond/draw/DrawHandler";
import { CTS_STYLE_ID } from "fond/draw/modes/cts/constants";
import { createStyles, getCTSStyles, removeCTSStyles } from "fond/draw/modes/cts/ctsDrawStyles";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { MapContext } from "fond/map/MapProvider";
import { zoomTo } from "fond/map/redux";
import { PolygonSelectButton, SelectButton, ToolbarConfig } from "fond/map/Toolbar";
import { EditMode, selectFeature, setEditMode } from "fond/project/redux";
import { featureCollection } from "fond/turf";
import { WORKFLOW_KEYS } from "fond/types";
import { Currency } from "fond/types/currency";
import { formatCurrency } from "fond/utils/currency";
import { filterFeatureCollection } from "fond/utils/filterExpressions";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { isInRange } from "fond/utils/number";
import { AgGrid, useStackedNavigationContext } from "fond/widgets";
import SkeletonLoadingGrid from "fond/widgets/SkeletonLoadingGrid";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

import CsvPreviewModal from "./CsvPreviewModal";
import PrioritizeButton from "./PrioritizeButton";

// Custom slider label to show currency values in tooltip without overflow issues due widget size.
function ValueLabelComponent(props: SliderValueLabelProps) {
  const { children, open, value } = props;

  return (
    <Tooltip
      open={open}
      title={value}
      PopperProps={{
        disablePortal: false,
      }}
    >
      {children}
    </Tooltip>
  );
}

interface ExcludeDrawPanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
  workflowKey: typeof WORKFLOW_KEYS.NBN_COST_TO_SERVE.ITERATION | typeof WORKFLOW_KEYS.COLLABORATION_CTS.ITERATION;
  layerFilter: any[] | undefined;
  source: string;
  sourceLayer: string;
  identifierLabel?: string;
  identifierProperty?: string;
  /**
   * Flag indicating whether to color the service locations by a Cost to Serve heat map.
   * Used in conjunction with colorByOptions property.
   * @default false
   */
  colorByCTS?: boolean;
  /**
   * Options for coloring service locations if colorByCTS is true.
   */
  colorByOptions?: { label: string; layerId: string; propertyKey: string }[];
  /**
   * Optional currency for formatting slider labels - if not provided
   * @default USD
   */
  currency?: Currency;
}

const config: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="polygonSelect" />],
  },
  {
    id: "excludeTools",
    type: "mode",
    items: [<PrioritizeButton key="prioritize" prioritize />, <PrioritizeButton key="defer" prioritize={false} />],
  },
];

const ExcludeDrawPanel: React.FC<ExcludeDrawPanelProps> = ({
  isReadOnly,
  workflowKey,
  layerFilter,
  source,
  sourceLayer,
  identifierLabel = "Equipment ID",
  identifierProperty = "equipment_id",
  colorByCTS = false,
  colorByOptions = [],
  currency = "USD",
}) => {
  const dispatch = useAppDispatch();
  const { drawControl, map, setDrawMode } = useContext(MapContext);
  const { goBack } = useStackedNavigationContext();
  const { enqueueSnackbar } = useSnackbar();
  const [rowData, setRowData] = useState<Feature[]>([]);
  const excludedFondIDs = useMemo(() => rowData.map(({ properties }) => properties?.FondID).filter(Boolean) as string[], [rowData]);
  const versionId = useAppSelector((state) => state.project.versionId);
  const { isLoading: isLoadingConfig } = useGetVersionConfigQuery(versionId);
  const versionConfig = useAppSelector((state) => selectVersionConfig(state, versionId));
  const { runWorkflow } = useCustomWorkflow({ versionId, key: workflowKey, refreshVersionDataOnFail: true });
  const { params } = useStackedNavigationContext();
  const scenarioName = params.excludeDraw?.scenarioName || "Untitled Scenario";
  const [colorBy, setColorBy] = useState<string | undefined>(colorByOptions.at(0)?.layerId);
  const [filterPropertyKey, setFilterPropertyKey] = useState<string>(colorByOptions.at(0)?.propertyKey || "");
  const [isWaitingForWorkflowStart, setIsWaitingForWorkflowStart] = useState<boolean>(false);

  const isCollaborationCTS = workflowKey === WORKFLOW_KEYS.COLLABORATION_CTS.ITERATION;

  // For Collaboration CTS: use the costing layer (AccCost) which includes cost properties needed for styling/filtering
  // For NBN: use the source layer directly
  const targetLayerKey = isCollaborationCTS ? `output/collaboration_cost_to_serve/${sourceLayer}/AccCost` : sourceLayer;
  const targetLayer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: targetLayerKey }));

  const { data, isLoading } = useGetFeaturesQuery({ layerId: targetLayer?.ID ?? "" }, { skip: !targetLayer?.ID });

  // CSV Modal state
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  // Bounds and slider state for three cost types
  const [accCostBounds, setAccCostBounds] = useState<{ min: number; max: number } | null>(null);
  const [accCostRange, setAccCostRange] = useState<number[]>([0, 0]);
  const [sharedCostBounds, setSharedCostBounds] = useState<{ min: number; max: number } | null>(null);
  const [sharedCostRange, setSharedCostRange] = useState<number[]>([0, 0]);
  const [indivCostBounds, setIndivCostBounds] = useState<{ min: number; max: number } | null>(null);
  const [indivCostRange, setIndivCostRange] = useState<number[]>([0, 0]);

  // Convert fetched features to initial features for DrawHandler
  const initialFeatures = useMemo(() => {
    let features = data ?? [];

    // Apply layer filter if present
    if (layerFilter) {
      try {
        const filtered = filterFeatureCollection(featureCollection(features), layerFilter);
        features = filtered.features;
      } catch (error) {
        console.error("Filter error:", error);
      }
    }

    // For Collaboration CTS: promote the ID from properties since we're using the costing layer
    // (the actual feature ID we want to exclude is stored in properties.id)
    // For NBN: use features as-is since we're already using the source layer
    if (isCollaborationCTS) {
      return features.map((feature) => ({
        ...feature,
        id: feature.properties?.id ?? feature.id,
      }));
    }

    return features;
  }, [data, layerFilter, isCollaborationCTS]);

  useMount(() => {
    dispatch(setEditMode(EditMode.edit));
  });

  useUnmount(() => {
    dispatch(setEditMode(EditMode.none));
    if (colorByCTS && map) removeCTSStyles(map);
  });

  useEffect(() => {
    if (colorByCTS && colorBy && map && versionConfig) {
      // Remove any existing CTS styles
      removeCTSStyles(map);
      const styles = getCTSStyles(versionConfig, colorBy, excludedFondIDs, source);

      if (styles.length === 0) {
        console.error("No CTS styles found for colorBy:", colorBy);
        return;
      }

      // Add the styles to the map
      styles.forEach((style) => {
        try {
          map.addLayer(style, CTS_STYLE_ID);
        } catch (error) {
          console.error(`Failed to add CTS layer ${style.id}:`, error);
        }
      });
    }
    // Note: isLoading is required in deps to ensure styles are applied after vector tile source is ready
  }, [colorBy, colorByCTS, map, excludedFondIDs, isLoading, isLoadingConfig, versionConfig, source, sourceLayer]);

  useEffect(() => {
    if (!initialFeatures || initialFeatures.length === 0) {
      setAccCostBounds(null);
      setAccCostRange([0, 0]);
      setSharedCostBounds(null);
      setSharedCostRange([0, 0]);
      setIndivCostBounds(null);
      setIndivCostRange([0, 0]);
      return;
    }

    const calculateBounds = (propertyKey: string) => {
      const bounds = initialFeatures.reduce(
        (acc, f) => {
          const value = f.properties?.[propertyKey];
          if (typeof value === "number") {
            if (value < acc.min) acc.min = value;
            if (value > acc.max) acc.max = value;
          }
          return acc;
        },
        { min: Infinity, max: -Infinity }
      );

      // Handle case where no numeric values were found
      if (bounds.min === Infinity || bounds.max === -Infinity) {
        return { min: 0, max: 0 };
      }

      return bounds;
    };

    // Calculate bounds for all three cost types
    const accBounds = calculateBounds("AccCost");
    const sharedBounds = calculateBounds("SharedCost");
    const indivBounds = calculateBounds("IndivCost");

    // Only update bounds and reset slider if bounds have actually changed
    setAccCostBounds((prev) => {
      const changed = !prev || prev.min !== accBounds.min || prev.max !== accBounds.max;
      if (changed) setAccCostRange([accBounds.min, accBounds.max]);
      return accBounds;
    });

    setSharedCostBounds((prev) => {
      const changed = !prev || prev.min !== sharedBounds.min || prev.max !== sharedBounds.max;
      if (changed) setSharedCostRange([sharedBounds.min, sharedBounds.max]);
      return sharedBounds;
    });

    setIndivCostBounds((prev) => {
      const changed = !prev || prev.min !== indivBounds.min || prev.max !== indivBounds.max;
      if (changed) setIndivCostRange([indivBounds.min, indivBounds.max]);
      return indivBounds;
    });
  }, [initialFeatures]);

  const drawingStyles = useMemo(
    () => createStyles({ styleId: CTS_STYLE_ID, source, sourceLayer, layerFilter, withHeatmap: colorByCTS }),
    [layerFilter, source, sourceLayer, colorByCTS]
  );

  const sources: FeatureIdentifier[] = useMemo(() => [{ source, sourceLayer }], [source, sourceLayer]);

  const columns: ColDef[] = useMemo(
    () =>
      [
        {
          headerName: `${identifierLabel} (${rowData.length})`,
          field: `properties.${identifierProperty}`,
          flex: 1,
          suppressHeaderMenuButton: true,
          resizable: false,
        },
      ] as ColDef[],
    [identifierLabel, identifierProperty, rowData.length]
  );

  const gridOptions: GridOptions = useMemo(
    () => ({
      animateRows: true,
      rowGroupPanelShow: "never",
      sideBar: false,
      pagination: false,
      domLayout: "normal",
    }),
    []
  );

  const handleOnSelectionChange = useCallback(
    (event: MapboxDraw.DrawSelectionChangeEvent) => {
      if (event.features.length === 1) {
        dispatch(selectFeature(event.features[0]));
      }
    },
    [dispatch]
  );

  const handleColorByChange = (event: SelectChangeEvent<string>) => {
    setColorBy(event.target.value);
    setFilterPropertyKey(colorByOptions.find((option) => option.layerId === event.target.value)?.propertyKey || "");
  };

  const handleOnRender = useCallback(() => {
    const allFeatures = drawControl.current?.getAll().features || [];
    const excludedFeatures = allFeatures.filter(({ properties }) => properties?.excluded === "true");
    setRowData(excludedFeatures);
  }, [drawControl]);

  // Updates the exclusion status for the provided feature IDs
  const updateFeatureExclusion = useCallback(
    (featureIds: string[], action: "prioritize" | "defer") => {
      // Determine excluded value based on action
      // prioritize = "false" (not excluded), defer = "true" (excluded)
      const excludedValue = action === "prioritize" ? "false" : "true";

      featureIds.forEach((featureId) => {
        // Ensure featureId is a string
        if (typeof featureId !== "string") return;

        const feature = drawControl.current?.get(featureId);

        if (feature) {
          // Update existing feature
          drawControl.current?.setFeatureProperty(featureId, "isEditing", true);
          drawControl.current?.setFeatureProperty(featureId, "excluded", excludedValue);
        }
      });

      // Update rowData which will automatically update excludedFondIDs via useMemo
      handleOnRender();

      // We refresh the features to force a redraw
      if (drawControl.current) {
        drawControl.current.set(drawControl.current.getAll());
      }
    },
    [drawControl, handleOnRender]
  );

  const handleAccCostSliderChange = useCallback((_: Event, value: number | number[]) => {
    setAccCostRange(value as number[]);
  }, []);

  const handleSharedCostSliderChange = useCallback((_: Event, value: number | number[]) => {
    setSharedCostRange(value as number[]);
  }, []);

  const handleIndivCostSliderChange = useCallback((_: Event, value: number | number[]) => {
    setIndivCostRange(value as number[]);
  }, []);

  const sliderConfig = useMemo(
    () => ({
      AccCost: {
        label: "Accumulated",
        bounds: accCostBounds,
        range: accCostRange,
        onChange: handleAccCostSliderChange,
        testId: "acc-cost-range-slider",
      },
      SharedCost: {
        label: "Shared",
        bounds: sharedCostBounds,
        range: sharedCostRange,
        onChange: handleSharedCostSliderChange,
        testId: "shared-cost-range-slider",
      },
      IndivCost: {
        label: "Individual",
        bounds: indivCostBounds,
        range: indivCostRange,
        onChange: handleIndivCostSliderChange,
        testId: "indiv-cost-range-slider",
      },
    }),
    [
      accCostBounds,
      accCostRange,
      handleAccCostSliderChange,
      sharedCostBounds,
      sharedCostRange,
      handleSharedCostSliderChange,
      indivCostBounds,
      indivCostRange,
      handleIndivCostSliderChange,
    ]
  );

  const handleSliderChangeCommitted = useCallback(() => {
    // Get all features from the draw control
    const allFeatures = drawControl.current?.getAll().features || [];

    // Separate features into those inside and outside the ranges
    const featuresToDefer: string[] = [];
    const featuresToPrioritize: string[] = [];

    allFeatures.forEach((feature) => {
      const accCost = feature.properties?.AccCost;
      const sharedCost = feature.properties?.SharedCost;
      const indivCost = feature.properties?.IndivCost;
      const isExcluded = feature.properties?.excluded === "true";

      if (typeof feature.id === "string") {
        // Check if all cost values are inside their respective ranges
        const isInsideRange =
          isInRange(accCost, accCostRange[0], accCostRange[1]) &&
          isInRange(sharedCost, sharedCostRange[0], sharedCostRange[1]) &&
          isInRange(indivCost, indivCostRange[0], indivCostRange[1]);

        if (!isInsideRange && !isExcluded) {
          // Feature has at least one cost outside range but not excluded - defer it
          featuresToDefer.push(feature.id);
        } else if (isInsideRange && isExcluded) {
          // All costs are inside range but feature is excluded - prioritize (un-defer) it
          featuresToPrioritize.push(feature.id);
        }
      }
    });

    // Apply changes
    if (featuresToDefer.length > 0) {
      updateFeatureExclusion(featuresToDefer, "defer");
    }
    if (featuresToPrioritize.length > 0) {
      updateFeatureExclusion(featuresToPrioritize, "prioritize");
    }
  }, [drawControl, accCostRange, sharedCostRange, indivCostRange, updateFeatureExclusion]);

  // Handle modal confirm - extract IDs and update feature exclusion
  const handleCsvConfirm = useCallback(
    (ids: string[], action: "prioritize" | "defer") => {
      updateFeatureExclusion(ids, action);

      const actionText = action === "prioritize" ? "Prioritized" : "Deferred";
      enqueueSnackbar(`${actionText} ${ids.length} features`, { variant: "success" });
    },
    [updateFeatureExclusion, enqueueSnackbar]
  );

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelectionChange);
    map?.on("draw.prioritize", handleOnRender);

    return () => {
      map?.off("draw.selectionchange", handleOnSelectionChange);
      map?.off("draw.prioritize", handleOnRender);
    };
  }, [dispatch, handleOnRender, handleOnSelectionChange, map]);

  const handleOnConfirm = async () => {
    // Set flag to wait for workflow to start before navigating back
    setIsWaitingForWorkflowStart(true);

    const payload = rowData.filter(({ id }) => typeof id === "string").map(({ id }) => ({ ID: id }));
    await runWorkflow?.({
      FeatureSelection: {
        Mode: "exclude",
        Features: {
          Type: "id",
          Ids: payload,
        },
      },
      TargetVersionName: scenarioName,
    });

    handleBack();
  };

  /**
   * Callback function called when a row is double clicked
   * Feature is zoomed to & selected
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    drawControl.current?.changeMode("simple_select", { featureIds: [event.data.properties.id] });
    dispatch(zoomTo([event.data]));
    dispatch(selectFeature(event.data));
  };

  const handleBack = () => {
    setDrawMode("no_feature");
    goBack();
  };

  if (isLoading) {
    return <SkeletonLoadingGrid spacing={2} items={[{ height: 40 }, { height: 130 }, { height: 38 }, { height: 300 }]} />;
  }

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Exclude service locations" />
      <Box px={1}>
        <Typography variant="caption">
          Choose the service locations you'd like to exclude from this scenario. When you're finished, click "Confirm" to proceed.
        </Typography>
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 2, px: 1 }}>
        Exclude by file:
      </Typography>
      <Button fullWidth variant="outlined" size="small" sx={{ mt: 2 }} onClick={() => setCsvModalOpen(true)}>
        <CloudUpload sx={{ mr: 1 }} />
        Browse
      </Button>

      {colorByCTS && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2, px: 1 }}>
            Exclude by cost:
          </Typography>
          <Box sx={{ mt: 2 }} display="flex" alignItems="center">
            <Select value={colorBy} onChange={handleColorByChange} fullWidth size="small" sx={{ ml: 1 }}>
              {colorByOptions.map(({ label, layerId }) => (
                <MenuItem key={layerId} value={layerId}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </>
      )}
      {/* Cost Range Slider - shows only the slider for the selected cost type */}
      {colorByCTS &&
        (() => {
          const currentSlider = sliderConfig[filterPropertyKey as keyof typeof sliderConfig];

          return currentSlider?.bounds ? (
            <Box sx={{ my: 2 }}>
              <Typography variant="caption" component="p" lineHeight={1.5} sx={{ mb: 0.5, mx: 1 }}>
                Range ({formatCurrency(currentSlider.range[0], { currency })} - {formatCurrency(currentSlider.range[1], { currency })})
              </Typography>
              <Box px={2}>
                <Slider
                  data-testid={currentSlider.testId}
                  min={currentSlider.bounds.min}
                  max={currentSlider.bounds.max}
                  value={currentSlider.range}
                  onChange={currentSlider.onChange}
                  onChangeCommitted={handleSliderChangeCommitted}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => formatCurrency(value, { currency })}
                  track="normal"
                  size="small"
                  slots={{
                    valueLabel: ValueLabelComponent,
                  }}
                />
              </Box>
              <Divider sx={{ my: 2 }} />
            </Box>
          ) : null;
        })()}

      <Box sx={{ height: 300, width: "100%", mt: 2 }}>
        <AgGrid
          rowData={rowData}
          columnDefs={columns}
          gridOptions={gridOptions}
          variant="outlined"
          size="compact"
          onRowDoubleClicked={onRowDoubleClicked}
        />
      </Box>
      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
        <Button color="primary" size="small" onClick={handleBack} disabled={isWaitingForWorkflowStart}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          size="small"
          onClick={handleOnConfirm}
          sx={{ ml: 1, px: 2 }}
          data-testid="finish-button"
          disabled={rowData.length === 0 || isReadOnly}
          loading={isWaitingForWorkflowStart}
        >
          Confirm and run new scenario
        </LoadingButton>
      </Box>
      {data !== undefined && (
        <DrawHandler
          sources={sources}
          startingMode="simple_select"
          initialFeatures={initialFeatures}
          config={config}
          styles={drawingStyles}
          disableHoverEffects
        />
      )}
      <CsvPreviewModal open={csvModalOpen} onClose={() => setCsvModalOpen(false)} onConfirm={handleCsvConfirm} features={initialFeatures} />
    </>
  );
};

export default ExcludeDrawPanel;
